import { MessageFilled } from "@ant-design/icons";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import {
    Market,
    TaskPromoTagPhotos,
    TaskRoute,
    TaskStatus,
} from "tm-shared/types";
import { uniqueByKey } from "tm-shared/utils";

import { BottomSheet } from "@/components/BottomSheet";
import { StatusCycleButton } from "@/components/CycleButton";
import FlexContainer from "@/components/FlexContainer";
import { GalleryUploader } from "@/components/GalleryUploader";
import Header from "@/components/Header";
import { Loading } from "@/components/Loading";
import { OutlineIconButton } from "@/components/OutlineIconButton";
import { SVScanButton } from "@/components/SVScanButton";
import { PrimaryText } from "@/components/Typography/Styled";
import { Viewfinder } from "@/components/UI/Viewfinder";
import { TASK_SCREEN_RECORDING_USER_IDS } from "@/config";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useActiveScrollNavigator } from "@/hooks/useActiveScrollNavigator";
import useAsyncAction from "@/hooks/useAsyncAction";
import { useCheckCommentOnReject } from "@/hooks/useCheckCommentOnReject";
import { useCompleteTask } from "@/hooks/useCompleteTask";
import { useMarketDistance } from "@/hooks/useMarketDistance";
import { makeCommand, useMakeCommand } from "@/hooks/useRequestQueue";
import { useStickyFilteredItems } from "@/hooks/useStickyFilteredItems";
import { useTaskCompletion } from "@/hooks/useTaskCompletion";
import { useTaskConfigSelector } from "@/hooks/useTaskSelectors";
import { TaskFinishButton } from "@/pages/WorkPlan/Tasks/components/TaskFinishButton";
import { openReplayService } from "@/Services/OpenReplay";
import {
    mutatePromoTagPhotosPhotos,
    patchPromoTagPhotosEntity,
} from "@/store/slices/progress";
import { addModalToQueue, setMdTasksConfig } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";
import { buildPhotoCaptions } from "@/utils/photo/captions";
import { getPhotoIdsFromPhotoTask } from "@/utils/photo/getPhotoIdsFromPhotoTask";

import { CameraPreviewOverlay } from "./components/CameraPreviewOverlay";
import { PromoTagPhotosController } from "./components/PromoTagPhotosController";
import { TaskPromoTag } from "./components/TaskPromoTag";
import {
    StyledBottomSheetTop,
    StyledList,
    StyledMainContainer,
} from "./Styled";

interface PromoTagPhotosPageProps {
    market: Market;
    task: TaskPromoTagPhotos;
}

export const PromoTagPhotosPage: FC<PromoTagPhotosPageProps> = ({
    task,
    market,
}) => {
    const { id: userId } = useAppSelector((s) => s.user);

    const dispatch = useAppDispatch();

    const marketId = market.id;
    const taskId = task.id;

    const { products: catalogProducts } = useAppSelector((s) => s.offline);

    const { firstName, lastName } = useAppSelector((s) => s.user);
    const { promoTagPhotos } = useAppSelector((s) => s.progress);

    const { activeItem, showUncompleted } = useTaskConfigSelector({
        marketId,
        taskId,
    });
    const safeActiveItem = activeItem ?? "";

    const productPayloadsMap = promoTagPhotos[taskId];
    const activePayload = productPayloadsMap?.[safeActiveItem];
    const activeStatus = activePayload?.status ?? TaskStatus.Incomplete;

    const taskProducts = useMemo(
        () => uniqueByKey(task.context.products, "barcode"),
        [task.context.products],
    );

    useEffect(() => {
        if (TASK_SCREEN_RECORDING_USER_IDS.includes(userId)) {
            openReplayService.startTaskRecording(taskId);
        }
    }, []);

    const prevBarcodeRef = useRef<string | null>(null);

    const onCloseBottomSheet = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                marketId,
                taskId,
                value: null,
            }),
        );
    }, [dispatch, marketId, taskId]);

    const handlerMessage = useCallback(() => {
        if (activeStatus === TaskStatus.Incomplete || !activeStatus) return;

        dispatch(
            addModalToQueue({
                type: ModalType.Comment,
                id: ModalType.Comment,
                payload: {
                    initialComment: activePayload?.comment ?? "",
                    route: TaskRoute.PromoTagPhotos,
                    taskId,
                    barcode: safeActiveItem,
                    taskStatus: activeStatus,
                },
            }),
        );
    }, [
        activeStatus,
        dispatch,
        activePayload?.comment,
        taskId,
        safeActiveItem,
    ]);

    const onClickInfoButton = useCallback(() => {
        dispatch(
            addModalToQueue({
                type: ModalType.TaskInfo,
                id: ModalType.TaskInfo,
                payload: {
                    title: "Фото акційних цінників",
                    description:
                        "Зробіть фото акційних цінників для кожного товару. На фото має бути видно товар і його акційну ціну. Якщо фото неможливо зробити — виберіть статус і додайте коментар.",
                },
            }),
        );
    }, [dispatch]);

    const resetStatusIfNeeded = useCallback(
        (barcode: string) => {
            const payload = productPayloadsMap?.[barcode];

            if (!payload) return;

            if (payload.status === TaskStatus.Rejected && !payload.comment) {
                dispatch(
                    patchPromoTagPhotosEntity({
                        taskId,
                        barcode,
                        patch: { status: TaskStatus.Incomplete },
                    }),
                );
            }
        },
        [productPayloadsMap, dispatch, taskId],
    );

    const onChangeActive = useCallback(
        (barcode: string | null) => {
            dispatch(
                setMdTasksConfig({
                    marketId,
                    taskId,
                    value: barcode,
                }),
            );
        },
        [dispatch, marketId, taskId],
    );

    const onChangeTaskStatus = useCallback(
        (value: TaskStatus, barcode?: string) => {
            const targetBarcode = barcode ?? activeItem;

            dispatch(
                patchPromoTagPhotosEntity({
                    taskId,
                    barcode: targetBarcode,
                    patch: { status: value, comment: "" },
                }),
            );
        },
        [activeItem, dispatch, taskId],
    );

    const onUploadPhoto = useCallback(
        (photo: string) => {
            if (!activeItem) return;

            dispatch(
                mutatePromoTagPhotosPhotos({
                    taskId,
                    barcode: activeItem,
                    option: "add",
                    photo,
                }),
            );
        },
        [activeItem, dispatch, taskId],
    );

    const onDeletePhoto = useCallback(
        (photoId: string) => {
            if (!activeItem) return;

            dispatch(
                mutatePromoTagPhotosPhotos({
                    taskId,
                    barcode: activeItem,
                    option: "remove",
                    photoId,
                }),
            );
        },
        [activeItem, dispatch, taskId],
    );

    const { validateAndNotify } = useCheckCommentOnReject();

    const handleCheckCommentOnReject = useCallback(
        (barcode: string | null) => {
            const prevDmpId = prevBarcodeRef.current ?? "";
            const payload = productPayloadsMap?.[prevDmpId];

            if (validateAndNotify(payload)) {
                return;
            }

            onChangeActive(barcode);
        },
        [productPayloadsMap, onChangeActive, validateAndNotify],
    );

    const { filteredItems: filteredTasks } = useStickyFilteredItems({
        items: taskProducts,
        showUncompleted: showUncompleted ?? false,
        getItemId: (task) => task.barcode,
        payloadsMap: productPayloadsMap ?? {},
        isIncomplete: (payload) => {
            return !payload || payload.status === TaskStatus.Incomplete;
        },
    });

    const { getItemRef, onNext, onPrev, scrollToActive } =
        useActiveScrollNavigator({
            ids: filteredTasks.map((t) => t.barcode),
            activeId: safeActiveItem,
            onChangeActive: handleCheckCommentOnReject,
        });

    const activeProductName = catalogProducts[safeActiveItem]?.name ?? "";

    useEffect(() => {
        const prev = prevBarcodeRef.current;

        if (prev && activeItem !== prev) {
            resetStatusIfNeeded(prev);
        }

        prevBarcodeRef.current = activeItem ?? null;
    }, [activeItem, resetStatusIfNeeded]);

    const { makeTaskCompleteCommands, makeUploadPhotoCommands } =
        useMakeCommand();
    const completeTask = useCompleteTask({ marketId, taskId });

    const [handleComplete, completeLoading] = useAsyncAction(async () => {
        const photoIds = getPhotoIdsFromPhotoTask(productPayloadsMap);
        const hasPhoto = photoIds.length > 0;

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: { taskId, hasPhoto, payload: productPayloadsMap },
            marketId,
        });

        const commands = [
            ...makeUploadPhotoCommands(photoIds),

            ...taskCompleteCommands,

            makeCommand({
                type: "updatePhotoStorage",
                title: "Синхронізація сховища",
                params: photoIds,
                options: {
                    stopOnError: false,
                },
            }),
        ];

        completeTask({ commands });

        if (TASK_SCREEN_RECORDING_USER_IDS.includes(userId)) {
            openReplayService.stopTaskRecording(taskId, productPayloadsMap);
        }
    });

    const { isAllCompleted } = useTaskCompletion({
        payloadMap: productPayloadsMap,
        expectedCount: taskProducts.length,
    });

    const { distance } = useMarketDistance({ marketGeo: market.geo });

    const captions = useMemo(
        () =>
            buildPhotoCaptions({
                marketName: market.name,
                taskName: task.name,
                userFullName: `${firstName} ${lastName}`,
                distance,
            }),
        [distance, firstName, lastName, market.name, task.name],
    );

    useEffect(() => {
        scrollToActive();
    }, [isAllCompleted]);

    return (
        <>
            <Header
                back
                title={task.name}
                rightActions={
                    <>
                        <SVScanButton marketId={marketId} taskId={taskId} />
                    </>
                }
            />

            <PromoTagPhotosController market={market} task={task} />

            <StyledMainContainer>
                <StyledList style={{ overflowY: "auto" }}>
                    {filteredTasks.map((product) => {
                        const { barcode } = product;
                        const currentDMPPayload = productPayloadsMap?.[barcode];
                        const isActive = barcode === activeItem;

                        return (
                            <div key={barcode} ref={getItemRef(barcode)}>
                                <TaskPromoTag
                                    product={product}
                                    catalogProduct={catalogProducts[barcode]}
                                    isActive={isActive}
                                    onChangeActive={onChangeActive}
                                    payload={currentDMPPayload}
                                    onChangeTaskStatus={onChangeTaskStatus}
                                />
                            </div>
                        );
                    })}
                </StyledList>

                <TaskFinishButton
                    onClick={handleComplete}
                    visible={isAllCompleted}
                    padding="8px 16px"
                    loading={completeLoading}
                />
            </StyledMainContainer>

            <BottomSheet
                open={!!activeItem}
                onClose={onCloseBottomSheet}
                onNext={onNext}
                onPrev={onPrev}
                onClickInfoButton={onClickInfoButton}
            >
                <StyledBottomSheetTop>
                    <PrimaryText>{activeProductName}</PrimaryText>
                    <FlexContainer gap="20px">
                        <OutlineIconButton
                            icon={<MessageFilled />}
                            onClick={handlerMessage}
                            disabled={activeStatus === TaskStatus.Incomplete}
                        />
                        <StatusCycleButton
                            value={activeStatus}
                            onChange={onChangeTaskStatus}
                            states={[
                                TaskStatus.Incomplete,
                                TaskStatus.Rejected,
                            ]}
                            disabled={activeStatus === TaskStatus.Completed}
                        />
                    </FlexContainer>
                </StyledBottomSheetTop>

                <GalleryUploader
                    previewTitle={safeActiveItem}
                    onDelete={onDeletePhoto}
                    onUpload={onUploadPhoto}
                    activeItemId={safeActiveItem}
                    disabled={activeStatus === TaskStatus.Rejected}
                    marketId={marketId}
                    savedPhotos={activePayload?.photos ?? []}
                    captionLines={captions}
                    cameraProps={{
                        viewfinder: Viewfinder,
                        previewOverlay: (
                            <CameraPreviewOverlay barcode={activeItem} />
                        ),
                    }}
                />
            </BottomSheet>

            {completeLoading && <Loading title="Завершення задачі" />}
        </>
    );
};
